import { Button, Chip, Icons, Stack, Typography } from '@healthinal/ui';
import { createFileRoute, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { getAppointmentStartDateTime } from '../../api/created/appointment.ts';
import { useGetAppointments } from '../../api/generated/appointment-controller/appointment-controller.ts';
import {
  getGetTracingContactQueryKey,
  useGetTracingContactSuspense,
} from '../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import {
  AppointmentDto,
  JournalDtoTopic,
  PatientBasicsDto,
  TracingContactDetailsDtoAssessmentStatus,
} from '../../api/generated/model';
import {
  getGetPatientBasicsByIdQueryOptions,
  useGetPatientBasicsByIdSuspense,
} from '../../api/generated/patient-controller/patient-controller.ts';
import { queryClient } from '../../api/queryClient.ts';
import { Breadcrumbs } from '../../components/Breadcrumbs.tsx';
import { ContentSection } from '../../components/ContentSection.tsx';
import { ErrorBoundary } from '../../components/errors/ErrorBoundary.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import {
  getTracingContactAssessmentResultFromStatus,
  getTracingContactAssessmentStatusColor,
  getTracingContactAssessmentStatusName,
} from '../../utils/contactTracing.ts';
import { formatDateWithGermanMonth, formatDateYear, isAfterNow } from '../../utils/date.ts';
import { CONTACT_TRACING_DOCUMENT_CATEGORIES } from '../../utils/document.ts';
import { AgeAndAgeCategoryAtCreation } from '../components/AgeAndAgeCategoryAtCreation.tsx';
import { CreateAppointmentDialog } from '../components/appointment/CreateAppointmentDialog.tsx';
import { CreateJournalEntryDialog } from '../components/journal/CreateJournalEntryDialog.tsx';
import { CreateDocumentDialog } from '../patients/components/CreateDocumentDialog.tsx';
import { UploadDocumentDialog } from '../patients/components/UploadDocumentDialog.tsx';
import { ContactTracingSideNavigation } from './$patientId/components/ContactTracingSideNavigation.tsx';
import { SetTracingAssessmentResultDialog } from './$patientId/components/SetTracingAssessmentResultDialog.tsx';
import { WaitingPeriodConfirmationDialog } from './$patientId/components/WaitingPeriodConfirmationDialog.tsx';

export const Route = createFileRoute('/contact-tracing/$patientId')({
  component: ContactTracingPatientLayout,
  loader: async ({ params: { patientId } }) =>
    await queryClient.ensureQueryData(getGetPatientBasicsByIdQueryOptions(patientId)),
  staticData: ({ loaderData: patient }: { loaderData: PatientBasicsDto }) => ({
    title: `${patient.firstName} ${patient.lastName}`,
  }),
});

function ContactTracingPatientLayout() {
  const { patientId } = Route.useParams();
  const { data: patient } = useGetPatientBasicsByIdSuspense(patientId);
  const { data: tracingDetails } = useGetTracingContactSuspense(patientId);
  const { data: appointments } = useGetAppointments({ patientId });
  const [isCreateAppointmentDialogShown, setIsCreateAppointmentDialogShown] = useState(false);
  const [isSetStatusDialogOpen, setIsSetStatusDialogOpen] = useState(false);
  const [isJournalDialogOpen, setIsJournalDialogOpen] = useState(false);
  const [isWaitingPeriodConfirmationDialogOpen, setIsWaitingPeriodConfirmationDialogOpen] = useState(false);
  const [isCreateDocumentDialogOpen, setIsCreateDocumentDialogOpen] = useState(false);
  const [isUploadDocumentDialogOpen, setIsUploadDocumentDialogOpen] = useState(false);
  const contentSectionRef = useRef(null);
  const navigate = useNavigate();
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const isDocumentRoute = currentMatch.routeId === '/contact-tracing/$patientId/document';

  return (
    <Stack direction="row" gap={6}>
      <ContactTracingSideNavigation patientId={patientId} />
      <ContentSection ref={contentSectionRef} flex={1} gap={2}>
        <StickyContainer>
          <Stack direction="row" alignItems="start" justifyContent="space-between">
            <Stack>
              <Typography
                level="h1"
                sx={{ cursor: patient.personId ? 'pointer' : 'default' }}
                onClick={() =>
                  patient.personId &&
                  navigate({ to: '/masterdata/persons/$personId', params: { personId: patient.personId } })
                }
                endDecorator={
                  <Typography fontSize={36} fontWeight={400} textColor={'text.tertiary'}>
                    {formatDateYear(patient.birthDate)}
                  </Typography>
                }>
                {patient.lastName.toUpperCase()} {patient.firstName}
              </Typography>
              <Typography level={'body-lg'}>
                Alter: <AgeAndAgeCategoryAtCreation patient={patient} />
              </Typography>
              <Breadcrumbs />
            </Stack>
            {isDocumentRoute ? (
              <Stack direction="row" gap={2}>
                <Button
                  color="neutral"
                  endDecorator={<Icons.UploadFile />}
                  onClick={() => setIsUploadDocumentDialogOpen(true)}>
                  Dokument hochladen
                </Button>
                <Button
                  color="neutral"
                  endDecorator={<Icons.Add />}
                  onClick={() => setIsCreateDocumentDialogOpen(true)}>
                  Dokument erstellen
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" gap={1}>
                <Button color="neutral" onClick={() => setIsJournalDialogOpen(true)} endDecorator={<Icons.Notes />}>
                  Notiz erfassen
                </Button>
                <Button
                  color="neutral"
                  endDecorator={<Icons.CalendarMonth />}
                  onClick={() => setIsCreateAppointmentDialogShown(true)}>
                  Termin erstellen
                </Button>
                {tracingDetails.assessmentStatus !== TracingContactDetailsDtoAssessmentStatus.RESULT_POSITIVE &&
                  tracingDetails.assessmentStatus !== TracingContactDetailsDtoAssessmentStatus.RESULT_NEGATIVE && (
                    <Button
                      endDecorator={<Icons.Timelapse />}
                      color="warning"
                      onClick={() => setIsWaitingPeriodConfirmationDialogOpen(true)}>
                      Inkubationszeit aktivieren
                    </Button>
                  )}
                <Button endDecorator={<Icons.Flaky />} onClick={() => setIsSetStatusDialogOpen(true)}>
                  Abschluss
                </Button>
              </Stack>
            )}
          </Stack>
          {!isDocumentRoute && (
            <Stack direction="row" gap={1} pb={2}>
              {tracingDetails.assessmentStatus && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontWeight="bold">Contact Tracing Status:</Typography>
                  <Chip
                    color={getTracingContactAssessmentStatusColor(tracingDetails.assessmentStatus)}
                    variant="solid"
                    sx={{ fontWeight: 'bold' }}>
                    {getTracingContactAssessmentStatusName(tracingDetails.assessmentStatus)}
                    {tracingDetails.assessmentStatus === TracingContactDetailsDtoAssessmentStatus.APPOINTMENT_VISIT &&
                      appointments &&
                      getAppointmentStatusText(appointments)}
                  </Chip>
                </Stack>
              )}
              {tracingDetails.incubationPeriodEnd &&
                tracingDetails.assessmentStatus === TracingContactDetailsDtoAssessmentStatus.WAITING_PERIOD && (
                  <Typography>(endet am {formatDateWithGermanMonth(tracingDetails.incubationPeriodEnd)})</Typography>
                )}
              {tracingDetails.assessmentNotes && <Typography>{tracingDetails.assessmentNotes}</Typography>}
            </Stack>
          )}
          <MotionDivider contentSectionRef={contentSectionRef} />
        </StickyContainer>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </ContentSection>
      <CreateAppointmentDialog
        preselectedPatient={patient}
        open={isCreateAppointmentDialogShown}
        onClose={() => {
          void queryClient.invalidateQueries({ queryKey: getGetTracingContactQueryKey(patientId) });
          setIsCreateAppointmentDialogShown(false);
        }}
      />
      <SetTracingAssessmentResultDialog
        patientId={patientId}
        open={isSetStatusDialogOpen}
        onClose={() => setIsSetStatusDialogOpen(false)}
        initialResult={
          tracingDetails.assessmentStatus != null
            ? getTracingContactAssessmentResultFromStatus(tracingDetails.assessmentStatus)
            : null
        }
        initialNotes={tracingDetails.assessmentNotes}
      />
      <CreateJournalEntryDialog
        patientId={patientId}
        preselectedTopic={JournalDtoTopic.TRACING_CONTACT}
        open={isJournalDialogOpen}
        onClose={() => setIsJournalDialogOpen(false)}
        disabledTopics={[
          JournalDtoTopic.NONE,
          JournalDtoTopic.CONTACT_TRACING,
          JournalDtoTopic.ANAMNESIS,
          JournalDtoTopic.THERAPY,
          JournalDtoTopic.DIAGNOSTICS,
          JournalDtoTopic.PERSONAL_DETAILS,
          JournalDtoTopic.NETWORK,
          JournalDtoTopic.SCREENING_PERSON,
        ]}
      />
      <WaitingPeriodConfirmationDialog
        open={isWaitingPeriodConfirmationDialogOpen}
        onClose={() => setIsWaitingPeriodConfirmationDialogOpen(false)}
        patientId={patientId}
        tracingContactDetails={tracingDetails}
      />
      <CreateDocumentDialog
        patientId={patientId}
        categories={CONTACT_TRACING_DOCUMENT_CATEGORIES}
        open={isCreateDocumentDialogOpen}
        onClose={() => setIsCreateDocumentDialogOpen(false)}
      />
      <UploadDocumentDialog
        patientId={patientId}
        categories={CONTACT_TRACING_DOCUMENT_CATEGORIES}
        open={isUploadDocumentDialogOpen}
        onClose={() => setIsUploadDocumentDialogOpen(false)}
      />
    </Stack>
  );
}

function getAppointmentStatusText(appointments: AppointmentDto[]): string {
  const attendedOrPendingAppointments = appointments.filter((appointment) => appointment.isAttended !== false);

  if (attendedOrPendingAppointments.length === 0) {
    return ' nötig';
  }

  const hasPlannedAppointments = attendedOrPendingAppointments.some((appointment) =>
    isAfterNow(getAppointmentStartDateTime(appointment)),
  );
  const appointmentCountText = ` (${appointments.length})`;
  const appointmentText = hasPlannedAppointments ? ' geplant' : ' besucht';
  return appointmentText + appointmentCountText;
}
